import type { ApolloCache, ApolloClient } from '@apollo/client';
import type { Zone } from '@seek/audience-zones';
import type { Language } from '@seek/ca-myprofile-utils';

import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import { mergeItemIntoListById } from 'src/modules/utils';
import * as Schema from 'src/types/graphql';

type ConfirmedJobsDbQualifications = NonNullable<
  Schema.GetJobsDbConfirmedQualificationsQuery['viewer']
>['confirmedQualifications'];

const getConfirmedQualificationDataFromCache = <T extends Record<string, any>>({
  cache,
  languageCode,
  showQualificationLevel2,
  zone,
  isUnifiedSite,
}: {
  cache: ApolloCache<T>;
  showQualificationLevel2: boolean;
} & Omit<Schema.ConfirmQualificationMutationVariables, 'qualification'>) =>
  (getDataFromCache({
    cache,
    query: showQualificationLevel2
      ? Schema.GetJobsDbConfirmedQualificationsDocument
      : Schema.GetConfirmedQualificationsDocument,
    variables: { languageCode, zone, isUnifiedSite },
  })?.viewer?.confirmedQualifications || []) as ConfirmedJobsDbQualifications;

const getUnconfirmedQualificationDataFromCache = <
  T extends Record<string, any>,
>({
  cache,
  languageCode,
  zone,
  isUnifiedSite,
}: {
  cache: ApolloCache<T>;
} & Omit<Schema.ConfirmQualificationMutationVariables, 'qualification'>) =>
  getDataFromCache({
    cache,
    query: Schema.GetUnconfirmedQualificationsDocument,
    variables: { languageCode, zone, isUnifiedSite },
  })?.viewer?.unconfirmedQualifications || [];

const buildConfirmedQualificationsOptimisticResponse = ({
  qualificationUpdate: { id, name, institute, level2, completed, highlights },
  apolloClient,
  languageCode,
  showQualificationLevel2,
  zone,
  isUnifiedSite,
}: {
  qualificationUpdate: Schema.UpdateQualificationInput;
  apolloClient: ApolloClient<any>;
  languageCode: Language;
  showQualificationLevel2: boolean;
  zone: Zone;
  isUnifiedSite: boolean;
}) => [
  ...mergeItemIntoListById({
    list: getConfirmedQualificationDataFromCache({
      ...apolloClient,
      languageCode,
      showQualificationLevel2,
      zone,
      isUnifiedSite,
    }),
    item: {
      __typename: 'Qualification',
      id,
      name: {
        __typename: 'OntologyStructuredData',
        ...name,
      },
      institute: {
        __typename: 'OntologyStructuredData',
        ...institute,
      },
      completed,
      completionDate: null,
      formattedCompletion: '',
      highlights: highlights || null,
      level2,
      status: null,
      credential: null,
      verificationUrl: null,
    },
    addToFrontOfList: true,
  }),
];

export const generateUpdateQualificationOptimisticResponse =
  ({
    _id,
    apolloClient,
    showQualificationLevel2,
  }: {
    _id: string;
    apolloClient: ApolloClient<any>;
    showQualificationLevel2: boolean;
  }) =>
  ({
    qualification,
    languageCode,
    zone,
    isUnifiedSite,
  }: Schema.UpdateQualificationMutationVariables) => ({
    __typename: 'Mutation',
    updateQualification: {
      __typename: 'UpdateQualificationPayload',
      viewer: {
        __typename: 'Candidate',
        _id,
        confirmedQualifications: buildConfirmedQualificationsOptimisticResponse(
          {
            qualificationUpdate: qualification,
            apolloClient,
            languageCode: languageCode as Language,
            showQualificationLevel2,
            zone,
            isUnifiedSite,
          },
        ),
      },
    },
  });

export const generateDeleteQualificationOptimisticResponse =
  ({
    _id,
    apolloClient,
    showQualificationLevel2,
  }: {
    _id: string;
    apolloClient: ApolloClient<any>;
    showQualificationLevel2: boolean;
  }) =>
  ({
    qualificationId,
    languageCode,
    zone,
    isUnifiedSite,
  }: Schema.DeleteQualificationMutationVariables) => {
    const confirmedQualifications = getConfirmedQualificationDataFromCache({
      ...apolloClient,
      languageCode: languageCode as Language,
      showQualificationLevel2,
      zone,
      isUnifiedSite,
    });

    return {
      __typename: 'Mutation',
      deleteQualification: {
        __typename: 'DeleteQualificationPayload',
        viewer: {
          __typename: 'Candidate',
          _id,
          confirmedQualifications: confirmedQualifications.filter(
            (qualification) => qualification.id !== qualificationId.id,
          ),
        },
      },
    };
  };

export const generateConfirmQualificationOptimisticResponse =
  ({
    _id,
    apolloClient,
    showQualificationLevel2,
  }: {
    _id: string;
    apolloClient: ApolloClient<any>;
    showQualificationLevel2: boolean;
  }) =>
  ({
    qualification,
    languageCode,
    zone,
    isUnifiedSite,
  }: Schema.ConfirmQualificationMutationVariables) => {
    const unconfirmedQualifications = getUnconfirmedQualificationDataFromCache({
      ...apolloClient,
      languageCode: languageCode as Language,
      zone,
      isUnifiedSite,
    });

    return {
      __typename: 'Mutation',
      confirmQualification: {
        __typename: 'ConfirmQualificationPayload',
        viewer: {
          __typename: 'Candidate',
          _id,
          unconfirmedQualifications: unconfirmedQualifications.filter(
            (unconfirmedQualification) =>
              unconfirmedQualification.id !== qualification.id,
          ),
          confirmedQualifications:
            buildConfirmedQualificationsOptimisticResponse({
              qualificationUpdate: qualification,
              apolloClient,
              languageCode: languageCode as Language,
              showQualificationLevel2,
              zone,
              isUnifiedSite,
            }),
        },
      },
    };
  };

export const generateRejectUnconfirmedDataOptimisticResponse =
  ({ _id, apolloClient }: { _id: string; apolloClient: ApolloClient<any> }) =>
  ({
    qualification: { id: rejectedQualificationId },
    languageCode,
    zone,
    isUnifiedSite,
  }: Schema.RejectQualificationMutationVariables) => {
    const unconfirmedQualifications = getUnconfirmedQualificationDataFromCache({
      ...apolloClient,
      languageCode: languageCode as Language,
      zone,
      isUnifiedSite,
    });

    return {
      __typename: 'Mutation',
      rejectUnconfirmedData: {
        __typename: 'RejectUnconfirmedDataPayload',
        viewer: {
          __typename: 'Candidate',
          _id,
          unconfirmedQualifications: unconfirmedQualifications.filter(
            (qualification) => qualification.id !== rejectedQualificationId,
          ),
        },
      },
    };
  };
